<template>
  <div>
    <h1 class="text-center text-base font-semibold mb-3">Create Session</h1>

    <!-- Mode: Select existing session -->
    <div v-if="mode === 'select'" class="flex flex-col gap-2.5">
      <p class="text-xs text-gray-600">We found existing sessions. You can select one to continue or create a new one.</p>
      <div class="flex flex-col gap-1.5">
        <label for="session-select" class="text-xs font-medium">Existing Sessions</label>
        <Select
          v-model="selectedSession"
          :options="existingSessions"
          optionLabel="name"
          placeholder="Select an existing session"
          class="w-full"
        />
      </div>
      <div class="flex justify-end gap-1.5 mt-2">
        <Button label="Use Selected Session" icon="pi pi-check" size="small" @click="selectExisting" :disabled="!selectedSession" />
        <Button label="Create New" icon="pi pi-plus" size="small" severity="secondary" @click="mode = 'create'" />
      </div>
    </div>

    <!-- Mode: Create new session -->
    <form v-else @submit.prevent="onSubmit" class="flex flex-col gap-2.5">
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-gray-700">Session Name</label>
        <AutoComplete
          v-model="formData.name"
          :suggestions="filteredSuggestions"
          @complete="filterSessions"
          placeholder="E.g., 2024/2025"
          class="w-full"
          inputClass="w-full"
        />
        <small v-if="errors.name" class="text-red-500">{{ errors.name }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-gray-700">Start Date</label>
        <Calendar
          v-model="formData.started_at"
          showTime
          hourFormat="24"
          placeholder="Select start date"
          class="w-full"
          :class="{ 'p-invalid': errors.started_at }"
        />
        <small v-if="errors.started_at" class="text-red-500">{{ errors.started_at }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-gray-700">End Date</label>
        <Calendar
          v-model="formData.ended_at"
          showTime
          hourFormat="24"
          placeholder="Select end date"
          class="w-full"
          :class="{ 'p-invalid': errors.ended_at }"
        />
        <small v-if="errors.ended_at" class="text-red-500">{{ errors.ended_at }}</small>
      </div>

      <div class="flex items-center justify-between mt-2">
        <Button v-if="props.existingSessions.length > 0" label="Back to Select" size="small" severity="secondary" @click="mode = 'select'" text/>
        <Button
          type="submit"
          label="Create Session"
          size="small"
          :loading="isSubmitting"
          class="!bg-primary !border-primary"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import type { Session } from '@/types/composition-functions';

const emit = defineEmits(['submit', 'select-session']);

const props = defineProps<{
  formData: {
    started_at: string | Date | null
    ended_at: string | Date | null,
    name: string
  }
  searchQuery: string
  filteredSessions: string[]
  existingSessions: Session[]
  errors: Record<string, string>
}>();

const mode = ref<'select' | 'create'>('create');
const selectedSession = ref<Session | null>(null);

watchEffect(() => {
  if (props.existingSessions && props.existingSessions.length > 0) {
    mode.value = 'select';
  } else {
    mode.value = 'create';
  }
});

const formData = ref(props.formData);
const filteredSuggestions = ref<string[]>([]);
const isSubmitting = ref(false);

function filterSessions(event: { query: string }) {
  filteredSuggestions.value = props.filteredSessions.filter((s) =>
    s.toLowerCase().includes(event.query.toLowerCase()),
  );
}

function selectExisting() {
  if (selectedSession.value) {
    emit('select-session', selectedSession.value.id);
  }
}

function onSubmit() {
  isSubmitting.value = true;
  emit('submit');
}
</script>
